import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ApiError, api } from '@/lib/api';
import './sign-in.css';

/**
 * Sign-in.
 *
 * Two-factor is a second step rather than a field on the first form: the server
 * only reveals that a TOTP code is required after the password verifies, so
 * showing the field up front would leak which accounts have 2FA enabled.
 *
 * Styled by sign-in.css — the Lumina system in standalone CSS with literal
 * light values, because no user preference exists before sign-in: this page
 * ignores the `dark` class and always renders light.
 */

/** The Artemis logo, served from /public so it is cached alongside the app. */
function Logo() {
  return <img className="auth-logo" src="/artemis-logo.svg" alt="Artemis" />;
}

/** Flat input with an optional show/hide toggle for password fields. */
function AuthInput({
  reveal,
  className,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & { reveal?: boolean }) {
  const [shown, setShown] = useState(false);

  if (!reveal) {
    return (
      <div className="auth-field">
        <input className={`auth-input${className ? ` ${className}` : ''}`} {...rest} />
      </div>
    );
  }

  return (
    <div className="auth-field auth-field--password">
      <input
        className={`auth-input${className ? ` ${className}` : ''}`}
        {...rest}
        type={shown ? 'text' : 'password'}
      />
      <button
        type="button"
        className="auth-reveal"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {shown ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

function SubmitButton({ isBusy, children }: { isBusy: boolean; children: React.ReactNode }) {
  return (
    <button type="submit" className="auth-submit" disabled={isBusy}>
      {isBusy ? <span className="auth-spinner" aria-label="Working" /> : children}
    </button>
  );
}

export function SignIn() {
  /**
   * Read once, then cleared: an idle logout should be explained on the screen
   * the user lands on, but not again the next time they visit sign-in.
   */
  const [timedOut] = useState(() => {
    const flag = sessionStorage.getItem('vf_signout_reason') === 'inactivity';
    if (flag) sessionStorage.removeItem('vf_signout_reason');
    return flag;
  });

  const { signIn, signOut, refresh, isAuthenticated, user, session } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'credentials' | 'twoFactor' | 'changePassword' | 'twoFactorSetup'>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  // "Remember this device": offered when an Admin has set a number of days.
  const [rememberDevice, setRememberDevice] = useState(false);
  const [rememberDays, setRememberDays] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  /* Resets are admin-driven — there is no self-serve endpoint — so the link
     explains who to ask rather than leading anywhere. */
  const [showResetNote, setShowResetNote] = useState(false);
  const [setup, setSetup] = useState<{ secret: string; qrDataUrl: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const setupStarted = useRef(false);

  // Two-factor is compulsory for every account (SEC-015) and the server
  // refuses a session everything else until it is set up, so it is done here,
  // before the app opens.
  const needsTwoFactorSetup = !!user && !user.totpEnabled;

  useEffect(() => {
    if (step === 'changePassword') return;
    if (!isAuthenticated) {
      if (step === 'twoFactorSetup') setStep('credentials');
      return;
    }
    if (needsTwoFactorSetup) setStep('twoFactorSetup');
    else if (step !== 'twoFactorSetup') navigate('/dashboard', { replace: true });
  }, [isAuthenticated, needsTwoFactorSetup, step, navigate]);

  // One secret per visit: a second request would replace the first on the
  // server while the first QR code was still on screen.
  useEffect(() => {
    if (step !== 'twoFactorSetup') {
      // Left the step (signed out, or the session ended): start afresh next time.
      setupStarted.current = false;
      setSetup(null);
      return;
    }
    if (setupStarted.current) return;
    setupStarted.current = true;
    api
      .post<{ secret: string; qrDataUrl: string }>('/api/auth/2fa/setup')
      .then(setSetup)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not start two-factor setup.'));
  }, [step]);

  const submitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsBusy(true);
    try {
      const res = await signIn(
        email,
        password,
        step === 'twoFactor' && !useBackup ? totpCode : undefined,
        step === 'twoFactor' && useBackup ? totpCode : undefined,
        step === 'twoFactor' && rememberDevice,
      );
      if (res.requiresTwoFactor) {
        setRememberDays(res.rememberDeviceDays ?? 0);
        setStep('twoFactor');
        return;
      }
      if (res.mustChangePassword) {
        setStep('changePassword');
        return;
      }
      if (res.requiresTwoFactorSetup) {
        setStep('twoFactorSetup');
        return;
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed. Please try again.');
    } finally {
      setIsBusy(false);
    }
  };

  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }
    setIsBusy(true);
    try {
      await api.post('/api/auth/change-password', { currentPassword: password, newPassword });
      // The server revokes every session on a password change, so the user
      // signs in again with the new credentials. The revoked session is
      // dropped here too, or the page would act on it.
      await signOut('revoked');
      setStep('credentials');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('Password changed. Please sign in with your new password.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the password.');
    } finally {
      setIsBusy(false);
    }
  };

  const submitSetupCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsBusy(true);
    try {
      const res = await api.post<{ backupCodes: string[] }>('/api/auth/2fa/verify', { code: totpCode, rememberDevice });
      setBackupCodes(res.backupCodes);
      setSetup(null);
      setTotpCode('');
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That code was not accepted.');
    } finally {
      setIsBusy(false);
    }
  };

  const heading =
    step === 'twoFactor'
      ? 'Enter your authentication code'
      : step === 'changePassword'
        ? 'Set a new password'
        : step === 'twoFactorSetup'
          ? backupCodes ? 'Save your backup codes' : 'Set up two-factor authentication'
          : 'Sign in';

  return (
    <div className="auth-page">
      <div className="auth-bg" aria-hidden="true" />

      <main className="auth-card">
        <Logo />
        <h1 className="auth-heading">{heading}</h1>

        {timedOut && step === 'credentials' && (
          <p className="auth-note auth-note--left" style={{ marginBottom: 16 }}>
            You were signed out because of inactivity. Sign in again to continue.
          </p>
        )}

        {step === 'twoFactorSetup' ? (
          backupCodes ? (
            <form
              className="auth-form"
              onSubmit={(e) => {
                e.preventDefault();
                navigate('/dashboard', { replace: true });
              }}
            >
              <p className="auth-note auth-note--left">
                Each code signs you in once if you lose your phone. Keep them somewhere safe, away from it. They
                won't be shown again.
              </p>
              <ul className="auth-codes" aria-label="Backup codes">
                {backupCodes.map((c) => <li key={c}>{c}</li>)}
              </ul>
              <SubmitButton isBusy={false}>I've saved them — continue</SubmitButton>
            </form>
          ) : (
            <form onSubmit={submitSetupCode} className="auth-form">
              <p className="auth-note auth-note--left">
                Two-factor is required for every account. Scan this with Google Authenticator or Microsoft
                Authenticator, then enter the six-digit code it shows.
              </p>
              {setup && (
                <>
                  <img src={setup.qrDataUrl} alt="Authenticator QR code" className="auth-qr" />
                  <p className="auth-note auth-note--left">
                    Can't scan it? Enter this key instead: <span className="auth-key">{setup.secret}</span>
                  </p>
                </>
              )}
              <AuthInput
                className="auth-input--code"
                placeholder="000000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                aria-label="Authentication code"
                required
              />
              {(session?.rememberDeviceDays ?? 0) > 0 && (
                <RememberDevice days={session!.rememberDeviceDays!} checked={rememberDevice} onChange={setRememberDevice} />
              )}
              {error && <p className="auth-error">{error}</p>}
              <SubmitButton isBusy={isBusy}>Verify and continue</SubmitButton>
              <div className="auth-links">
                <button type="button" className="auth-link" onClick={() => void signOut()}>
                  Sign out
                </button>
              </div>
            </form>
          )
        ) : step === 'changePassword' ? (
          <form onSubmit={submitNewPassword} className="auth-form">
            <AuthInput
              reveal
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              aria-label="New password"
              autoFocus
              required
            />
            <AuthInput
              reveal
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              aria-label="Confirm password"
              required
            />
            <p className="auth-note auth-note--left">
              At least 12 characters, with upper and lower case, a number and a symbol.
            </p>
            {error && <p className="auth-error">{error}</p>}
            <SubmitButton isBusy={isBusy}>Set password</SubmitButton>
          </form>
        ) : (
          <form onSubmit={submitCredentials} className="auth-form">
            {step === 'credentials' ? (
              <>
                <AuthInput
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  aria-label="Email address"
                  autoFocus
                  required
                />
                <AuthInput
                  reveal
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  aria-label="Password"
                  required
                />
              </>
            ) : (
              <AuthInput
                className={useBackup ? undefined : 'auth-input--code'}
                placeholder={useBackup ? 'Backup code' : '000000'}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                inputMode={useBackup ? 'text' : 'numeric'}
                maxLength={useBackup ? undefined : 6}
                autoComplete="one-time-code"
                aria-label={useBackup ? 'Backup code' : 'Authentication code'}
                autoFocus
                required
              />
            )}

            {step === 'twoFactor' && rememberDays > 0 && (
              <RememberDevice days={rememberDays} checked={rememberDevice} onChange={setRememberDevice} />
            )}

            {error && <p className="auth-error">{error}</p>}

            <SubmitButton isBusy={isBusy}>
              {step === 'twoFactor' ? 'Verify' : 'Sign in'}
            </SubmitButton>

            <div className="auth-links">
              {step === 'credentials' ? (
                <>
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => setShowResetNote((v) => !v)}
                  >
                    Forgot password?
                  </button>
                  {showResetNote && (
                    <p className="auth-note">
                      Ask your administrator to reset your password.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => {
                      setUseBackup((v) => !v);
                      setTotpCode('');
                    }}
                  >
                    {useBackup ? 'Use authenticator code instead' : 'Use a backup code instead'}
                  </button>
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => {
                      setStep('credentials');
                      setTotpCode('');
                      setError(null);
                    }}
                  >
                    Back
                  </button>
                </>
              )}
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

/** Skip the code on this browser for a while; only for this account. */
function RememberDevice({ days, checked, onChange }: { days: number; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="auth-remember">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      Remember this device for {days} days
    </label>
  );
}
